/**
 * View Evidence dialog: criteria summary and tabbed source documents.
 */

import { useEffect, useRef, useState } from "react";
import { closeModal, Modal, openModal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import { EvidencePdfViewer } from "./EvidencePdfViewer";
import { TextEvidencePanel } from "./TextEvidencePanel";
import {
  hasTextScreeningEvidence,
  type ViewEvidenceContext,
} from "./viewEvidenceTypes";

const DIALOG_ID = ModalId.VIEW_EVIDENCE_MODAL;

/** Props for the View Evidence modal. */
export interface ViewEvidenceModalProps {
  isOpen: boolean;
  context: ViewEvidenceContext | null;
  onClose: () => void;
}

/**
 * Label + body block for the left criteria column.
 *
 * @param props.label - Section heading
 * @param props.text - Section body copy
 * @returns Criteria summary block
 */
function CriteriaSection({
  label,
  text,
  isLast = false,
}: {
  label: string;
  text: string;
  isLast?: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-1 pb-4 ${
        isLast ? "" : "mb-4 border-b border-primaryGray-14"
      }`}
    >
      <div className="text-overline font-semibold uppercase tracking-[0.0625rem] text-primaryGray-8">
        {label}
      </div>
      <div className="text-xs leading-5 text-primaryGray-3">{text}</div>
    </div>
  );
}

/**
 * Show screening evidence with criteria copy and tabbed PDF sources.
 *
 * @param props - Open state, evidence context, and close handler
 * @returns View Evidence modal
 */
export function ViewEvidenceModal({
  isOpen,
  context,
  onClose,
}: ViewEvidenceModalProps) {
  const isModalOpenRef = useRef(false);
  const [activeSourceIndex, setActiveSourceIndex] = useState(0);
  const sourcePaths = context?.sourcePaths ?? [];

  useEffect(() => {
    if (isOpen) {
      setActiveSourceIndex(0);
    }
  }, [context, isOpen]);

  useEffect(() => {
    if (isOpen && !isModalOpenRef.current) {
      openModal(DIALOG_ID);
      isModalOpenRef.current = true;
    } else if (!isOpen && isModalOpenRef.current) {
      closeModal(DIALOG_ID);
      isModalOpenRef.current = false;
    }
  }, [isOpen]);

  useEffect(
    () => () => {
      if (isModalOpenRef.current) {
        closeModal(DIALOG_ID);
        isModalOpenRef.current = false;
      }
    },
    [],
  );

  /**
   * Close the oasis modal and notify the parent.
   */
  const handleClose = () => {
    closeModal(DIALOG_ID);
    isModalOpenRef.current = false;
    onClose();
  };

  if (!isOpen || !context) {
    return null;
  }

  const activePath = sourcePaths[activeSourceIndex];
  const showTextEvidence =
    sourcePaths.length === 0 &&
    hasTextScreeningEvidence(
      context.textEvidenceSource,
      context.textEvidenceDecidingFact,
    );

  return (
    <Modal
      dialogId={DIALOG_ID}
      title="View Evidence"
      onSave={handleClose}
      onCancel={handleClose}
      onClose={handleClose}
      hideFooter
      heightPercentage={88}
      saveButtonText="Close"
      cancelText="Close"
    >
      <div className="flex h-full min-h-0 w-screen max-w-full flex-col gap-3">
        <p className="text-sm font-semibold text-primaryGray-1">
          {context.criteriaTitle}
        </p>
        <div className="flex min-h-0 flex-1 gap-0 overflow-hidden rounded-lg border border-primaryGray-14">
          <div className="flex w-[17.5rem] shrink-0 flex-col overflow-y-auto border-r border-primaryGray-14 bg-white p-4">
            <CriteriaSection
              label="Primary Criteria"
              text={context.primaryCriteria}
            />
            <CriteriaSection label="Sub Criteria" text={context.subCriteria} />
            <CriteriaSection
              label="Patient Finding"
              text={context.patientFinding}
              isLast
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col bg-white">
            {sourcePaths.length > 0 ? (
              <>
                <div className="flex shrink-0 gap-6 border-b border-primaryGray-14 px-4">
                  {sourcePaths.map((_, index) => {
                    const active = index === activeSourceIndex;
                    return (
                      <button
                        key={`source-tab-${index}`}
                        type="button"
                        className={`border-b-2 py-3 text-sm font-semibold ${
                          active
                            ? "border-tertiaryBlue-4 text-tertiaryBlue-4"
                            : "border-transparent text-primaryGray-6"
                        }`}
                        onClick={() => setActiveSourceIndex(index)}
                      >
                        Source {index + 1}
                      </button>
                    );
                  })}
                </div>
                <div className="min-h-0 flex-1">
                  {activePath && <EvidencePdfViewer sourcePath={activePath} />}
                </div>
              </>
            ) : showTextEvidence ? (
              <TextEvidencePanel
                source={context.textEvidenceSource}
                decidingFact={context.textEvidenceDecidingFact}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center p-6 text-sm text-primaryGray-4">
                No source documents are available for this criterion.
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
